import { Component, inject } from '@angular/core';
import { AbstractControl, FormControl, ValidationErrors, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatList, MatListItem, MatListItemLine } from '@angular/material/list';
import { LetDirective } from '@ngrx/component';
import { Store } from '@ngrx/store';
import ObjectID from 'bson-objectid';

import { CompanyModel } from '@ninety/platform/models/company/company-model';
import { ErrorService, SessionService, extractValueFromStore } from '@ninety/platform/services';
import {
  CompanyService,
  CompanyUsersStateActions,
  CompanyUserListModel,
  SpinnerActions,
  selectCurrentUserId,
} from '@ninety/platform/state';
import {
  selectAllCompanyUsers,
  selectCompanyUserByCompanyId,
} from '@ninety/platform/state/app-entities/company-users/company-users-state.selectors';
import {
  TerraFormFieldModule,
  TerraTextInputModule,
  TerraIconModule,
  TerraErrorModule,
  TerraSearchInputModule,
} from '@ninety/terra';
import {
  ButtonComponent,
  CardComponent,
  CardHeaderComponent,
  NinetyTooltipDirective,
  SearchCompanyFilterPipe,
  LegacyAlertComponent as AlertComponent,
} from '@ninety/ui/core';
import { UserService } from '@ninety/ui/legacy/core/services/user.service';

@Component({
  selector: 'ninety-add-helpful',
  templateUrl: './add-helpful.component.html',
  styleUrls: ['../_shared/tab-styles.scss', './add-helpful.component.scss'],
  standalone: true,
  imports: [
    LetDirective,
    CardComponent,
    CardHeaderComponent,
    TerraFormFieldModule,
    TerraTextInputModule,
    FormsModule,
    ReactiveFormsModule,
    TerraIconModule,
    TerraErrorModule,
    AlertComponent,
    ButtonComponent,
    TerraSearchInputModule,
    MatList,
    MatListItem,
    MatListItemLine,
    NinetyTooltipDirective,
    SearchCompanyFilterPipe,
  ],
})
export class AddHelpfulComponent {
  private readonly store = inject(Store);
  private readonly companyService = inject(CompanyService);
  private readonly userService = inject(UserService);
  private readonly sessionService = inject(SessionService);
  private errorService = inject(ErrorService);

  protected readonly companyUserList$ = this.store.select(selectAllCompanyUsers);
  protected readonly currentUserId$ = this.store.select(selectCurrentUserId);

  companyToFindForm = new FormControl('', {
    validators: [
      (c: AbstractControl): ValidationErrors =>
        !c.value || ObjectID.isValid(c.value) ? null : { isObjectId: { value: c.value } },
    ],
  });
  companyFilterSearch = new FormControl('');

  foundCompany: CompanyUserListModel;
  alreadyInCompany = false;
  newCompanyUser: CompanyUserListModel;
  displayedColumns = ['logo', 'name', 'login', 'remove'];

  findCompanyById() {
    const companyId = this.companyToFindForm.value.trim();

    if (!ObjectID.isValid(companyId)) {
      this.companyToFindForm.markAsTouched();
      this.companyToFindForm.updateValueAndValidity();
      return;
    }

    const companyUser = extractValueFromStore(
      this.store,
      selectCompanyUserByCompanyId(companyId)
    ) as CompanyUserListModel;
    if (companyUser) {
      this.alreadyInCompany = true;
      this.foundCompany = companyUser;
      return;
    }

    this.companyService.getCompanyById(companyId).subscribe({
      next: company => {
        this.foundCompany = {
          userId: null,
          companyId: company._id,
          accountStatus: company.accountStatus,
          companyName: company.name,
          logoUrl: company.logo.url,
          bos: company.bos,
          company: company as unknown as CompanyModel,
        };
        this.alreadyInCompany = false;
        this.newCompanyUser = null;
        this.store.dispatch(SpinnerActions.stopPrimary({ source: 'add-helpful' }));
      },
    });
  }

  addHelpful() {
    this.userService.addHelpfulToCompany(this.foundCompany.companyId).subscribe({
      next: cu => {
        const entity: CompanyUserListModel = {
          companyId: cu.company._id,
          userId: cu._id,
          accountStatus: cu.company.accountStatus,
          companyName: cu.company.name,
          logoUrl: cu.company.logo.url,
          bos: cu.company.bos,
          company: cu.company as unknown as CompanyModel,
        };
        this.store.dispatch(CompanyUsersStateActions.addOne({ entity: entity }));
        this.newCompanyUser = entity;
      },
    });
  }

  removeHelpfulFromCompany(companyUser: CompanyUserListModel) {
    this.store.dispatch(SpinnerActions.startPrimary({ source: 'remove-helpful' }));

    this.userService.update({ deleted: true }, companyUser.userId).subscribe({
      next: () => {
        this.store.dispatch(CompanyUsersStateActions.removeOne({ userId: companyUser.userId }));
        this.store.dispatch(SpinnerActions.stopPrimary({ source: 'remove-helpful' }));
      },
    });
  }

  clearCompanyFilterSearch(): void {
    this.companyFilterSearch.setValue('');
  }

  switchCompany(nextUser: CompanyUserListModel): void {
    try {
      this.sessionService.switchCompany(nextUser);
    } catch (error) {
      this.errorService.notify(
        error,
        'There was an error switching companies, please try again or contact support',
        'Error Switching Company'
      );
    }
  }
}
